<template>
  <v-container style="max-width: 1540px" fluid>
    <MobileBanner v-if="isMobileBreakpoint" class="mb-4" />
    <AnalyticsBanner v-else style="margin-bottom: 24px" />
    <RealtimeModal v-if="openRealtimeModal" :open.sync="openRealtimeModal" />
    <div
      :class="[
        'analytics-layout-wrapper',
        {
          'd-flex justify-space-between':
            !isMobileBreakpoint && !isMediumBreakpoint,
        },
      ]"
      :style="{
        gap: !isMobileBreakpoint && !isMediumBreakpoint ? '24px' : undefined,
        flexDirection: isMediumBreakpoint ? 'column' : undefined,
      }"
    >
      <div
        ref="targetDiv"
        class="card-analytics-v3"
        :style="{
          flex:
            isMobileBreakpoint || isMediumBreakpoint ? '0 0 100%' : '0 0 70%',
          width: isMobileBreakpoint || isMediumBreakpoint ? '100%' : undefined,
          marginBottom: isMediumBreakpoint ? '24px' : undefined,
        }"
      >
        <div class="d-flex justify-space-between align-center mb-5">
          <p class="body-bold-medium neutral-20-dark--text text-break">
            {{
              isMobileBreakpoint
                ? $t('message.pages.analyticsV3.resume.mobile.summaryTitle')
                : $t('message.pages.analyticsV3.resume.summary')
            }}
          </p>
          <div>
            <RangeDatePicker
              v-if="!isMobileBreakpoint"
              ref="watched"
              :shortcuts="shortcuts()"
              :initial-range-date="initialRangeDate"
              :btn-text="$t('message.pages.analyticsV3.filter')"
              @fetch-range-date="fetchDate"
              :data-step="3"
              :data-intro="`<strong>${$t(
                'message.pages.analyticsV3.tours.resume.step3.title'
              )}</strong><br><br>${$t(
                'message.pages.analyticsV3.tours.resume.step3.description'
              )}`"
            />
          </div>
        </div>

        <v-row
          class="d-flex flex-wrap mb-5"
          :data-step="1"
          data-position="right"
          data-scroll-to="tooltip"
          :data-intro="`<strong>${$t(
            'message.pages.analyticsV3.tours.resume.step1.title'
          )}</strong><br><br>${$t(
            'message.pages.analyticsV3.tours.resume.step1.description'
          )}`"
        >
          <v-col
            v-for="cardItem in cardsItems"
            :key="cardItem.label"
            :cols="isMobileBreakpoint ? 6 : 4"
            class="d-flex"
          >
            <ResumeCard :item="cardItem" :loading="loading" />
          </v-col>
        </v-row>

        <template
          v-if="!isMobileBreakpoint"
          :data-step="6"
          :data-intro="`<strong>${$t(
            'message.pages.analyticsV3.tours.resume.step6.title'
          )}</strong><br><br>${$t(
            'message.pages.analyticsV3.tours.resume.step6.description'
          )}`"
        >
          <BaseTable
            :items="consumeData"
            class="mt-5 mb-2"
            :columns="consumeTableColumns"
            :loading="loading"
            :show-pagination="false"
            :showActionColumn="false"
            noPointer
          />

          <div class="mb-5">
            <base-btn
              type="secondary"
              size="small"
              @click="handleOpenLink('AnalyticsV3Bandwidth')"
            >
              {{ $t('message.pages.analyticsV3.resume.bandwidthDetails') }}
              <v-icon size="20" class="ml-1">mdi-arrow-right</v-icon>
            </base-btn>
          </div>
        </template>

        <template
          v-if="!isMobileBreakpoint"
          :data-step="7"
          :data-intro="`<strong>${$t(
            'message.pages.analyticsV3.tours.resume.step7.title'
          )}</strong><br><br>${$t(
            'message.pages.analyticsV3.tours.resume.step7.description'
          )}`"
        >
          <BaseTable
            v-if="!isMobileBreakpoint"
            class="mt-5 mb-2"
            :items="top5Videos"
            :columns="topVideosTableColumns"
            :loading="loading"
            :show-pagination="false"
            :showActionColumn="false"
            noPointer
          />

          <div v-if="!isMobileBreakpoint">
            <base-btn
              type="secondary"
              size="small"
              @click="handleOpenLink('AnalyticsV3Plays')"
            >
              {{ $t('message.pages.analyticsV3.resume.playDetails') }}
              <v-icon size="20" class="ml-1">mdi-arrow-right</v-icon>
            </base-btn>
          </div>
        </template>
      </div>

      <div
        v-if="!isMobileBreakpoint"
        :style="{
          flex: !isMediumBreakpoint ? '0 0 calc(30% - 24px)' : '0 0 100%',
          width: isMediumBreakpoint ? '100%' : undefined,
        }"
      >
        <RealTimeBanner
          :loading="loading"
          :real-time-users="realTimeUsers"
          class="mb-5"
          :data-step="4"
          :data-intro="`<strong>${$t(
            'message.pages.analyticsV3.tours.resume.step4.title'
          )}</strong><br><br>${$t(
            'message.pages.analyticsV3.tours.resume.step4.description'
          )}`"
          @open-realtime="handleOpenRealtimeModal"
        />
        <BlueBanner
          class="mb-5"
          :data-step="5"
          :data-intro="`<strong>${$t(
            'message.pages.analyticsV3.tours.resume.step5.title'
          )}</strong><br><br>${$t(
            'message.pages.analyticsV3.tours.resume.step5.description'
          )}`"
        />
        <MoreMetricsMenu />
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import RangeDatePicker from '@/components/RangeDatePicker'
import ResumeCard from '../../components/Charts/ResumeCard.vue'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import i18n from '@/translations/lang'
import { format, subDays } from 'date-fns'
import MoreMetricsMenu from '../../components/Banners/MoreMetricsMenu.vue'
import BlueBanner from '../../components/Banners/BlueBanner.vue'
import AnalyticsBanner from '../../components/Banners/AnalyticsBanner.vue'
import MobileBanner from '../../components/Banners/MobileBanner.vue'
import RealTimeBanner from '../../components/Banners/RealTimeBanner.vue'

export default {
  name: 'Resume',
  components: {
    ResumeCard,
    BaseTable,
    RangeDatePicker,
    MoreMetricsMenu,
    BlueBanner,
    AnalyticsBanner,
    MobileBanner,
    RealTimeBanner,
  },
  data() {
    return {
      plays: 0,
      playRate: 0,
      gbCost: 0,
      traffic: 0,
      traffic_drm: 0,
      traffic_download: 0,
      usersQnt: 0,
      realTimeUsers: 0,
      averageRetention: 0,
      loading: false,
      top5Videos: [],
      isMediumBreakpoint: false,
      isVisible: null,
      openRealtimeModal: false,
    }
  },
  computed: {
    ...mapState({
      userPlanInfo: (state) => state.user.userPlanInfo,
      dateRange: (state) => state.analytics.filters.dateRange,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    ...mapGetters('user', ['userLimit']),

    currencyPrefix() {
      return this.userPlanInfo.currency === 'brl' ? 'R$' : '$'
    },

    rangeDates() {
      return {
        startDate: format(this.dateRange.start, 'yyyy-MM-dd'),
        endDate: format(this.dateRange.end, 'yyyy-MM-dd'),
      }
    },

    averagePlayCost() {
      const value =
        (this.bytesToGB(this.traffic) * this.gbCost) / (this.plays || 1) / 100
      return value
    },

    averageUserCost() {
      if (this.usersQnt === 0) return 'N/A'
      const value =
        (this.bytesToGB(this.traffic) * this.gbCost) / this.usersQnt / 100
      return value
    },

    cardsItems() {
      return {
        plays: {
          label: this.$t('message.pages.analyticsV3.resume.plays'),
          tooltip: this.$t('message.pages.analyticsV3.resume.tooltips.plays'),
          value: this.plays.toLocaleString(i18n.language),
        },
        playRate: {
          label: this.$t('message.pages.analyticsV3.resume.playRate'),
          tooltip: this.$t(
            'message.pages.analyticsV3.resume.tooltips.playRate'
          ),
          value:
            this.playRate.toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }) + '%',
        },
        averageUserCost: {
          label: this.$t('message.pages.analyticsV3.resume.averageCostPerUser'),
          tooltip:
            this.averageUserCost === 0 || this.averageUserCost >= 1
              ? this.$t(
                  'message.pages.analyticsV3.resume.tooltips.averageCostPlay'
                )
              : this.$t(
                  'message.pages.analyticsV3.resume.tooltips.averageCostPerPlay'
                ),
          value: this.formatCost(this.averageUserCost, i18n.language),
        },
        usersQnt: {
          label: this.$t('message.pages.analyticsV3.resume.users'),
          tooltip: this.$t('message.pages.analyticsV3.resume.tooltips.users'),
          value: this.usersQnt.toLocaleString(i18n.language),
        },
        averageRetention: {
          label: this.$t('message.pages.analyticsV3.resume.averageRetention'),
          tooltip: this.$t(
            'message.pages.analyticsV3.resume.tooltips.averageRetention'
          ),
          value:
            this.averageRetention.toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }) + '%',
        },
        averagePlayCost: {
          label: this.$t('message.pages.analyticsV3.resume.averageCostPerPlay'),
          tooltip:
            this.averagePlayCost === 0 || this.averagePlayCost >= 1
              ? this.$t(
                  'message.pages.analyticsV3.resume.tooltips.averageCostUser'
                )
              : this.$t(
                  'message.pages.analyticsV3.resume.tooltips.averageCostPerPlay'
                ),
          value: this.formatCost(this.averagePlayCost, i18n.language),
        },
      }
    },

    consumeTableColumns() {
      return [
        {
          text: this.$t(
            'message.pages.analyticsV3.resume.bandwidthConsumption'
          ),
          value: 'name',
          align: 'start',
          fontSize: '16px',
          color: 'neutral-20-dark--text',
        },
        {
          text: this.$t('message.pages.analyticsV3.resume.consumption'),
          value: 'consume',
          align: 'end',
          color: 'neutral-20-dark--text',
          tooltip: this.$t(
            'message.pages.analyticsV3.resume.tooltips.consumption'
          ),
        },
      ]
    },

    consumeData() {
      return [
        {
          name: this.$t('message.pages.analyticsV3.resume.plays'),
          consume: this.bytesToSize(this.traffic),
        },
        {
          name: 'DRM',
          consume: this.bytesToSize(this.traffic_drm),
        },
        {
          name: this.$t('message.pages.analyticsV3.resume.download'),
          consume: this.bytesToSize(this.traffic_download),
        },
      ]
    },

    topVideosTableColumns() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.resume.topVideos'),
          value: 'name',
          align: 'start',
          fontSize: '16px',
          color: 'neutral-20-dark--text',
        },
        {
          text: this.$t('message.pages.analyticsV3.resume.plays'),
          value: 'plays',
          align: 'end',
          tooltip: this.$t('message.pages.analyticsV3.resume.tooltips.plays'),
          color: 'neutral-20-dark--text',
        },
      ]
    },
    initialRangeDate() {
      return [this.dateRange.start, this.dateRange.end]
    },
  },
  methods: {
    ...mapMutations('analytics', [
      'UPDATE_FILTER_DATA_RANGE',
      'UPDATE_ERROR_STATE',
    ]),

    translateNumberFormat(value, lang = 'en') {
      if (lang === 'pt') {
        value = value.replaceAll(',', '.')

        return value
      }

      value = value.replaceAll('.', ',')
      return value
    },

    checkBreakpoint() {
      const width = window.innerWidth
      this.isMediumBreakpoint = width >= 768 && width < 1100
    },

    fetchDate(event) {
      const [start, end] = event
      this.UPDATE_FILTER_DATA_RANGE({ start, end })
    },

    handleOpenLink(name) {
      this.$router.push({ name })
    },

    shortcuts() {
      const plan = this.userPlanInfo

      return [
        {
          text: this.$i18next.t(
            'message.pages.video.metricsPage.datepicker.yesterday'
          ),
          onClick() {
            const date = new Date()
            return [subDays(date, 1), subDays(date, 1)]
          },
        },
        {
          text: this.$i18next.t(
            'message.pages.analyticsV3.datapicker.currentCycle'
          ),
          onClick() {
            const startDate = new Date(
              plan.subscription.current_period_start * 1000
            )
            const endDate = new Date(
              plan.subscription.current_period_end * 1000
            )
            return [startDate, endDate]
          },
        },
        {
          text: this.$i18next.t(
            'message.pages.video.metricsPage.datepicker.7days'
          ),
          onClick() {
            const date = new Date()
            return [subDays(date, 7), date]
          },
        },
        {
          text: this.$i18next.t(
            'message.pages.video.metricsPage.datepicker.aMonth'
          ),
          onClick() {
            return [subDays(new Date(), 30), new Date()]
          },
        },
        {
          text: (() => {
            const now = new Date()
            const monthNames = [
              'january',
              'february',
              'march',
              'april',
              'may',
              'june',
              'july',
              'august',
              'september',
              'october',
              'november',
              'december',
            ]
            const lastMonthIndex =
              now.getMonth() - 1 < 0 ? 11 : now.getMonth() - 1
            const lastMonthName = monthNames[lastMonthIndex]
            return this.$i18next.t(`message.pages.credits.${lastMonthName}`)
          })(),
          onClick() {
            const now = new Date()
            const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
            return [firstDay, lastDay]
          },
        },
      ]
    },

    bytesToGB(bytes) {
      return bytes / 1024 ** 3
    },

    bytesToSize(bytes) {
      if (!bytes || isNaN(bytes) || bytes < 0) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const converted = (bytes / Math.pow(1024, i)).toFixed(2)
      return `${converted} ${sizes[i]}`
    },

    formatNumberToLocale(value, lang) {
      return value.toLocaleString(lang, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    formatCost(value, lang) {
      if (value === 0 || value >= 1) return 'N/A'
      const decimal = value.toString().split('.')[1]?.[0] ?? '0'
      const fixed = decimal === '0' ? 3 : 2
      return (
        this.currencyPrefix +
        ' ' +
        value.toLocaleString(lang, {
          minimumFractionDigits: fixed,
          maximumFractionDigits: fixed,
        })
      )
    },

    callEndpoints() {
      const startDate = this.rangeDates.startDate
      const endDate = this.rangeDates.endDate

      return Promise.all([
        newAnalyticsApi.getAccountMetricsV2(startDate, endDate),
        newAnalyticsApi.getRanking(startDate, endDate),
        newAnalyticsApi.getUsersMetrics(startDate, endDate),
        newAnalyticsApi.getAccountRetention(startDate, endDate),
        newAnalyticsApi.getTrafficBandwidth(startDate, endDate, 'all'),
        newAnalyticsApi.getRealtime(0, 0, 300),
      ])
    },

    handleFetchError(err) {
      this.UPDATE_ERROR_STATE(true)
      this.errors = {
        fetchError: true,
        tooltipErrMsg:
          err?.msg ??
          err?.response?.data?.errMsg ??
          err?.response?.data?.message ??
          'Error',
        shownErrMsg: i18n.t('message.errors.unexpectedError'),
      }
    },

    async fetchData() {
      try {
        this.loading = true
        const [
          accountMetricsResponse,
          rankingResponse,
          usersQuantityResponse,
          retentionResponse,
          trafficResponse,
          realTimeResponse,
        ] = await this.callEndpoints()

        this.handleAccountMetrics(accountMetricsResponse)
        this.handleRanking(rankingResponse)
        this.handleUsersQuantity(usersQuantityResponse)
        this.handleRetention(retentionResponse)
        this.handleTrafficBandwidth(trafficResponse.data.data)
        this.handleRealTime(realTimeResponse.data)
      } catch (error) {
        this.handleFetchError(error)
      } finally {
        this.loading = false
        this.checkVisible()
        this.updateDivPosition()
      }
    },

    handleAccountMetrics(response) {
      this.plays = response?.data?.total?.play ?? 0
      this.playRate =
        ((this.plays ?? 0) * 100) / (response.data?.total?.view || 1)
    },

    handleRanking(response) {
      this.top5Videos = []
      response.data
        .sort((a, b) => b.play - a.play)
        .slice(0, 5)
        .forEach((video) => {
          this.top5Videos.push({
            name: video.title,
            plays: video.play.toLocaleString(i18n.language),
          })
        })
    },

    handleUsersQuantity(response) {
      this.usersQnt = response.data.length
    },

    handleRetention(response) {
      let totalDuration = 1
      let totalEngagement = 0

      response.data.videos.forEach((video) => {
        totalDuration += video.duration ?? 0
        totalEngagement += video.engagement ?? 0
      })

      this.averageRetention = (totalEngagement * 100) / totalDuration
    },

    calculateAllTraffic(trafficOnDay) {
      let totalTraffic = 0
      let trafficDrm = 0
      let trafficDownload = 0

      trafficOnDay.forEach((day) => {
        totalTraffic += day.b
        trafficDrm += day.b_drm
        trafficDownload += day.b_download
      })

      return {
        total: totalTraffic,
        drm: trafficDrm,
        download: trafficDownload,
      }
    },

    handleTrafficBandwidth(response) {
      const calculatedTraffic = this.calculateAllTraffic(response)
      this.traffic = calculatedTraffic.total
      this.traffic_drm = calculatedTraffic.drm
      this.traffic_download = calculatedTraffic.download
    },

    handleRealTime(response) {
      this.realTimeUsers = response.total_users || 0
    },

    handleOpenRealtimeModal() {
      this.openRealtimeModal = true
    },

    checkVisible() {
      this.$nextTick(() => {
        const rect = this.$refs.watched.$el.getBoundingClientRect()
        const vh = window.innerHeight || document.documentElement.clientHeight
        const offset = 70

        const isVisible = rect.top < vh + offset && rect.bottom > offset

        this.isVisible = isVisible
        this.$root.$emit('isVisibleDatePickerOnResume', !isVisible)
      })
    },
    updateDivPosition() {
      this.$nextTick(() => {
        const rect = this.$refs.targetDiv.getBoundingClientRect()
        this.$root.$emit('updatePickerPositionOnResume', {
          value: rect.right,
          isMediumBreakpoint: this.isMediumBreakpoint,
        })
      })
    },
  },
  watch: {
    dateRange: {
      handler() {
        this.fetchData()
      },
    },
    userLimit: {
      immediate: true,
      handler(value) {
        if (value !== undefined && value !== null) {
          this.gbCost = value.gb_cost ?? 0
        }
      },
    },
  },
  mounted() {
    this.checkBreakpoint()
    window.addEventListener('resize', this.checkBreakpoint)
    this.fetchData()
    this.updateDivPosition()
    window.addEventListener('resize', this.updateDivPosition)
    this.checkVisible()
    window.addEventListener('scroll', this.checkVisible, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.checkVisible)
    window.removeEventListener('resize', this.checkBreakpoint)
    window.removeEventListener('resize', this.updateDivPosition)
  },
}
</script>

<style scoped lang="scss">
.main-devices-analytics-v3 {
  flex: 1;
  overflow: auto;
}

.container {
  padding: 12px 24px;
}

.card-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.text-break {
  white-space: pre-line;
}

.charts-container {
  margin: 24px -12px 0 -12px;

  .col {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
